import { getLeaveStatusLabel, getLeaveTypeLabel } from "@/lib/leave-i18n";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import type { Locale } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { CalendarDays, Clock, PlaneTakeoff, Sun, User, FileText, MapPin, Link as LinkIcon, Users2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetCalendarEvents,
  apiGetLeaveRequests,
  apiGetMyLeaveRequests,
  apiGetPublicHolidays,
  apiGetTenantConfig,
  apiGetMyLeaveRequest,
  apiGetLeaveRequest,
  type CalendarEvent,
  type LeaveRequest,
  type PublicHoliday,
} from "@/lib/api";

// Map free-text company country → public_holidays table country code
const COUNTRY_MAP: Record<string, string> = {
  "netherlands": "Netherlands", "holland": "Netherlands", "the netherlands": "Netherlands",
  "nl": "Netherlands", "nld": "Netherlands",
  "united kingdom": "UK", "uk": "UK", "great britain": "UK",
  "england": "UK", "gb": "UK", "gbr": "UK", "scotland": "UK", "wales": "UK",
  "north macedonia": "North Macedonia", "macedonia": "North Macedonia",
  "republic of north macedonia": "North Macedonia", "mk": "North Macedonia", "mkd": "North Macedonia",
  "united states": "US", "us": "US", "usa": "US", "america": "US",
};

function resolveHolidayCountry(raw: string | null | undefined): string {
  if (!raw) return "Netherlands";
  const key = raw.trim().toLowerCase();
  return COUNTRY_MAP[key] ?? "Netherlands";
}

type FilterTab = "all" | "events" | "leave" | "holidays";

interface ListEntry {
  id: string;
  kind: "event" | "leave" | "holiday";
  title: string;
  subtitle: string;
  dateKey: string;
  color: string;
  category?: string;
  leaveRequestId?: number;
  eventId?: string;
  /** Set for non-approved leaves (e.g. "Pending") so a status badge is shown. */
  statusLabel?: string;
}

// Maps the (plural) filter tab value to the (singular) ListEntry kind.
const FILTER_KIND: Record<Exclude<FilterTab, "all">, ListEntry["kind"]> = {
  events: "event",
  leave: "leave",
  holidays: "holiday",
};

const CATEGORY_COLORS: Record<string, string> = {
  meeting: "border-blue-500",
  announcement: "border-amber-500",
  celebration: "border-pink-500",
  company: "border-purple-500",
  bank_holiday: "border-red-500",
  other: "border-slate-400",
};

const STATUS_STYLES: Record<string, string> = {
  Approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  Pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  Rejected: "bg-red-500/10 text-red-600 border-red-500/30",
  Cancelled: "bg-gray-500/10 text-gray-500 border-gray-500/30",
  "In Review": "bg-blue-500/10 text-blue-600 border-blue-500/30",
};

function formatDateRange(start: string, end: string | null, locale: Locale) {
  try {
    const s = format(parseISO(start), "MMM d", { locale });
    if (!end || end === start) return s;
    const e = format(parseISO(end), "d MMM yyyy", { locale });
    return `${s} – ${e}`;
  } catch {
    return start;
  }
}

function LeaveDetailSheet({
  leaveId,
  open,
  onClose,
  isTeamView = false,
}: {
  leaveId: number | null;
  open: boolean;
  onClose: () => void;
  isTeamView?: boolean;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const { data: req, isLoading } = useQuery({
    queryKey: ["leave-request-detail", leaveId, isTeamView],
    queryFn: () => isTeamView ? apiGetLeaveRequest(leaveId!) : apiGetMyLeaveRequest(leaveId!),
    enabled: open && leaveId !== null,
    staleTime: 2 * 60_000,
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto pb-8">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-base flex items-center gap-2">
            <PlaneTakeoff className="h-4 w-4 text-primary" />
            {t("mobile.calendar.leaveRequest")}
          </SheetTitle>
        </SheetHeader>

        {isLoading || !req ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
            {isLoading ? t("common.loading", "Loading…") : t("common.noData")}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("mobile.calendar.status")}</span>
              <Badge variant="outline" className={`text-xs ${STATUS_STYLES[req.status] ?? ""}`}>
                {getLeaveStatusLabel(t, req.status)}
              </Badge>
            </div>

            {/* Leave type */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("mobile.calendar.leaveType")}</span>
              <span className="text-sm font-medium">{getLeaveTypeLabel(t, req.leaveType, req.leaveTypeLabel)}</span>
            </div>

            {/* Dates */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {t("mobile.calendar.dates")}
              </span>
              <span className="text-sm font-medium">
                {formatDateRange(req.startDate, req.endDate, dateLocale)}
              </span>
            </div>

            {/* Duration */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {t("mobile.calendar.duration")}
              </span>
              <span className="text-sm font-medium">{t("leave.duration", { count: req.workingDays })}</span>
            </div>

            {/* Replacement */}
            {req.replacementEmployeeName && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {t("leave.replacementEmployee")}
                </span>
                <span className="text-sm font-medium">{req.replacementEmployeeName}</span>
              </div>
            )}

            {/* Reviewer */}
            {req.reviewedByName && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("mobile.calendar.reviewedBy")}</span>
                <span className="text-sm font-medium">{req.reviewedByName}</span>
              </div>
            )}

            {/* Notes */}
            {req.notes && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-1">{t("leave.notes")}</p>
                <p className="text-sm">{req.notes}</p>
              </div>
            )}

            {/* Rejection reason */}
            {req.rejectionReason && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-red-500 mb-1">{t("mobile.calendar.rejectionReason")}</p>
                <p className="text-sm text-red-600">{req.rejectionReason}</p>
              </div>
            )}

            {/* Document */}
            {req.documentUrl && (
              <div className="pt-2 border-t border-border/50 flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <a
                  href={req.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline underline-offset-2"
                >
                  {req.documentOriginalName ?? t("mobile.calendar.viewDocument")}
                </a>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EventDetailDialog({
  event,
  open,
  onClose,
}: {
  event: CalendarEvent | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 gap-0 overflow-hidden max-w-[95vw] w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        {event && (
          <>
            {/* Cover image */}
            {event.coverImageUrl ? (
              <div className="w-full aspect-video bg-muted">
                <img src={event.coverImageUrl} alt={event.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className={`w-full h-24 ${CATEGORY_COLORS[event.category]?.replace("border-", "bg-") ?? "bg-slate-400"}/15`} />
            )}

            <div className="p-5 space-y-4">
              {/* Title + category */}
              <div className="space-y-2">
                <Badge variant="secondary" className="text-[10px] capitalize">
                  {event.category.replace("_", " ")}
                </Badge>
                <h2 className="text-lg font-bold text-foreground leading-tight pr-6">{event.title}</h2>
              </div>

              {/* Meta rows */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5 text-sm">
                  <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground">
                    {formatDateRange(event.date, event.endDate, dateLocale)}
                  </span>
                </div>
                {(event.time || event.allDay) && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-foreground">
                      {event.allDay ? t("mobile.calendar.allDay") : event.time}
                    </span>
                  </div>
                )}
                {event.location && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{event.location}</span>
                  </div>
                )}
                {event.organizer && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Users2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{event.organizer}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {event.description && (
                <div className="pt-2 border-t border-border/50">
                  <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{event.description}</p>
                </div>
              )}

              {/* Link */}
              {event.link && (
                <a
                  href={event.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary font-medium underline underline-offset-2"
                >
                  <LinkIcon className="h-4 w-4" />
                  {t("mobile.calendar.openLink")}
                </a>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MonthSection({
  month,
  items,
  onLeaveClick,
  onEventClick,
}: {
  month: string;
  items: ListEntry[];
  onLeaveClick: (id: number) => void;
  onEventClick: (id: string) => void;
}) {
  return (
    <div className="mb-5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
        {month}
      </p>
      <div className="space-y-2">
        {items.map((item) => {
          const clickable = (item.kind === "leave" && item.leaveRequestId != null) || (item.kind === "event" && item.eventId != null);
          return (
          <div
            key={item.id}
            onClick={() => {
              if (item.kind === "leave" && item.leaveRequestId != null) onLeaveClick(item.leaveRequestId);
              else if (item.kind === "event" && item.eventId != null) onEventClick(item.eventId);
            }}
            className={`bg-card border border-border/60 rounded-xl px-4 py-3 border-l-4 ${item.color} ${clickable ? "active:bg-muted/50 cursor-pointer" : ""}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">
                  {item.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.subtitle}</p>
              </div>
              {item.statusLabel ? (
                <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLES[item.statusLabel] ?? "bg-amber-500/10 text-amber-600 border-amber-500/30"}`}>
                  {item.statusLabel}
                </Badge>
              ) : item.category ? (
                <Badge variant="secondary" className="text-[10px] shrink-0 capitalize">
                  {item.category.replace("_", " ")}
                </Badge>
              ) : null}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

type CalendarView = "personal" | "team";

function CalendarContent({
  calendarEvents,
  leaves,
  publicHolidays,
  isTeamView,
  dateLocale,
  t,
  onLeaveClick,
  onEventClick,
}: {
  calendarEvents: CalendarEvent[];
  leaves: LeaveRequest[];
  publicHolidays: PublicHoliday[];
  isTeamView: boolean;
  dateLocale: Locale;
  t: TFunction;
  onLeaveClick: (id: number) => void;
  onEventClick: (id: string) => void;
}) {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");

  const allEntries = useMemo<ListEntry[]>(() => {
    const eventEntries: ListEntry[] = isTeamView ? [] : calendarEvents.map((e) => ({
      id: `ev-${e.id}`,
      kind: "event" as const,
      title: e.title,
      subtitle: [formatDateRange(e.date, e.endDate, dateLocale), e.time, e.location].filter(Boolean).join(" · "),
      dateKey: e.date,
      color: CATEGORY_COLORS[e.category] ?? CATEGORY_COLORS.other,
      category: e.category,
      eventId: e.id,
    }));

    const leaveEntries: ListEntry[] = leaves
      // Hide rejected/cancelled; show approved + pending-type leaves
      .filter((l) => l.status !== "Rejected" && l.status !== "Cancelled")
      .map((l) => {
        const isApproved = l.status === "Approved";
        return {
          id: `lv-${l.id}`,
          kind: "leave" as const,
          title: isTeamView ? `${l.employeeName} — ${getLeaveTypeLabel(t, l.leaveType, l.leaveTypeLabel)}` : getLeaveTypeLabel(t, l.leaveType, l.leaveTypeLabel),
          subtitle: formatDateRange(l.startDate, l.endDate, dateLocale),
          dateKey: l.startDate,
          color: isApproved ? "border-emerald-500" : "border-amber-500",
          leaveRequestId: l.id,
          // Show a status badge for anything not yet approved (Pending, In Review, …)
          statusLabel: isApproved ? undefined : l.status,
        };
      });

    const holidayEntries: ListEntry[] = isTeamView ? [] : publicHolidays.map((h) => ({
      id: `hd-${h.id}`,
      kind: "holiday" as const,
      title: h.name,
      subtitle: formatDateRange(h.date, null, dateLocale),
      dateKey: h.date,
      color: "border-red-400",
      category: "holiday",
    }));

    return [...eventEntries, ...leaveEntries, ...holidayEntries].sort((a, b) =>
      a.dateKey.localeCompare(b.dateKey)
    );
  }, [calendarEvents, leaves, publicHolidays, isTeamView, dateLocale, t]);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return allEntries;
    const kind = FILTER_KIND[activeFilter];
    return allEntries.filter((e) => e.kind === kind);
  }, [allEntries, activeFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ListEntry[]>();
    for (const entry of filtered) {
      let monthKey = "Unknown";
      try { monthKey = format(parseISO(entry.dateKey), "MMMM yyyy", { locale: dateLocale }); } catch {}
      if (!map.has(monthKey)) map.set(monthKey, []);
      map.get(monthKey)!.push(entry);
    }
    return Array.from(map.entries());
  }, [filtered, dateLocale]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Filter tabs */}
      <div className="px-4 pt-3 pb-2 shrink-0">
        <Tabs value={activeFilter} onValueChange={(v) => setActiveFilter(v as FilterTab)}>
          <TabsList className={`grid w-full ${isTeamView ? "grid-cols-2" : "grid-cols-4"}`}>
            <TabsTrigger value="all" className="text-xs">{t("mobile.calendar.all")}</TabsTrigger>
            {!isTeamView && (
              <TabsTrigger value="events" className="text-xs">
                <CalendarDays className="h-3 w-3 mr-1" />{t("mobile.calendar.events")}
              </TabsTrigger>
            )}
            <TabsTrigger value="leave" className="text-xs">
              <PlaneTakeoff className="h-3 w-3 mr-1" />{t("mobile.calendar.leave")}
            </TabsTrigger>
            {!isTeamView && (
              <TabsTrigger value="holidays" className="text-xs">
                <Sun className="h-3 w-3 mr-1" />{t("mobile.calendar.holidays")}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <CalendarDays className="h-10 w-10 opacity-30" />
            <p className="text-sm">{t("mobile.calendar.nothingToShow")}</p>
          </div>
        ) : (
          grouped.map(([month, items]) => (
            <MonthSection key={month} month={month} items={items} onLeaveClick={onLeaveClick} onEventClick={onEventClick} />
          ))
        )}
      </div>
    </div>
  );
}

export default function MobileCalendar() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const [calendarView, setCalendarView] = useState<CalendarView>("personal");
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetLeaveId, setSheetLeaveId] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventOpen, setEventOpen] = useState(false);

  const isHrOrAdmin = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";

  // Open the leave sheet (?request=) or event dialog (?event=) from a deep link
  useEffect(() => {
    const reqId = searchParams.get("request");
    if (reqId) {
      setSheetLeaveId(Number(reqId));
      setSheetOpen(true);
    }
    const evId = searchParams.get("event");
    if (evId) {
      setEventId(evId);
      setEventOpen(true);
    }
  }, [searchParams]);

  function openLeave(id: number) {
    setSheetLeaveId(id);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    if (searchParams.has("request")) {
      setSearchParams({}, { replace: true });
    }
  }

  function openEvent(id: string) {
    setEventId(id);
    setEventOpen(true);
  }

  function closeEvent() {
    setEventOpen(false);
    if (searchParams.has("event")) {
      setSearchParams({}, { replace: true });
    }
  }

  const { data: tenantConfig } = useQuery({
    queryKey: ["tenant-config"],
    queryFn: apiGetTenantConfig,
    staleTime: 60 * 60_000,
  });
  const country = resolveHolidayCountry(tenantConfig?.companyCountry);

  const { data: calendarEvents = [] } = useQuery({
    queryKey: ["mobile-calendar-events"],
    queryFn: apiGetCalendarEvents,
    staleTime: 10 * 60_000,
  });

  // Personal: own leaves of every status (approved shown plain, pending get a badge)
  const { data: myLeaves = [] } = useQuery({
    queryKey: ["mobile-my-leaves"],
    queryFn: () => apiGetMyLeaveRequests(),
    staleTime: 5 * 60_000,
  });

  // Team: all employees' approved leaves (HR/admin only)
  const { data: teamLeaves = [] } = useQuery({
    queryKey: ["mobile-team-approved-leaves"],
    queryFn: () => apiGetLeaveRequests({ status: "Approved" }),
    staleTime: 5 * 60_000,
    enabled: isHrOrAdmin,
  });

  const thisYear = new Date().getFullYear();
  const { data: holidaysThisYear = [] } = useQuery({
    queryKey: ["mobile-public-holidays", country, thisYear],
    queryFn: () => apiGetPublicHolidays(country, thisYear),
    staleTime: 60 * 60_000,
  });
  const { data: holidaysNextYear = [] } = useQuery({
    queryKey: ["mobile-public-holidays", country, thisYear + 1],
    queryFn: () => apiGetPublicHolidays(country, thisYear + 1),
    staleTime: 60 * 60_000,
  });
  const publicHolidays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...holidaysThisYear, ...holidaysNextYear].filter((h) => {
      try { return parseISO(h.date) >= today; } catch { return false; }
    });
  }, [holidaysThisYear, holidaysNextYear]);

  const isTeamView = calendarView === "team";

  return (
    <div className="flex flex-col h-full">
      {/* Top view switcher for HR/Admin */}
      {isHrOrAdmin && (
        <div className="px-4 pt-4 pb-1 shrink-0">
          <Tabs value={calendarView} onValueChange={(v) => setCalendarView(v as CalendarView)}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="personal" className="text-xs">
                <User className="h-3 w-3 mr-1.5" />
                {t("mobile.calendar.myCalendar")}
              </TabsTrigger>
              <TabsTrigger value="team" className="text-xs">
                <PlaneTakeoff className="h-3 w-3 mr-1.5" />
                {t("mobile.calendar.teamCalendar")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      <CalendarContent
        calendarEvents={calendarEvents}
        leaves={isTeamView ? teamLeaves : myLeaves}
        publicHolidays={publicHolidays}
        isTeamView={isTeamView}
        dateLocale={dateLocale}
        t={t}
        onLeaveClick={openLeave}
        onEventClick={openEvent}
      />

      <LeaveDetailSheet
        leaveId={sheetLeaveId}
        open={sheetOpen}
        onClose={closeSheet}
        isTeamView={isTeamView}
      />

      <EventDetailDialog
        event={calendarEvents.find((e) => e.id === eventId) ?? null}
        open={eventOpen}
        onClose={closeEvent}
      />
    </div>
  );
}
